import { ActionIcon, Indicator, Popover, Text, createStyles, Checkbox, Group, Anchor, Divider } from '@mantine/core';
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';

// Custom styles
const useStyles = createStyles((theme) => ({
	label: {
		fontSize: 14,
		lineHeight: '18px',
		fontWeight: 'bold',
		padding: 12
	},
	optionLabel: {
		textTransform: 'capitalize',
		fontSize: 14,
		lineHeight: '18px',
	},
	optionRow: {
		padding: '8px 12px',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: 'rgba(230, 210, 246, 0.5)'
		}
	},
	isSelected: {
		backgroundColor: 'rgba(230, 210, 246, 0.5)'
	}
}));

// Option to select
const Option = ({ text, action, active }) => {

	// State and hooks
	const { classes, cx } = useStyles();

	return (
		<Group
			className={cx(classes.optionRow, { [classes.isSelected]: active })}
			position="apart"
			onClick={action}
		>
			{/* Label */}
			<Text className={classes.optionLabel}>
				{text}
			</Text>
			{/* Checkbox */}
			<Checkbox
				radius="xl"
				size="xs"
				style={{ cursor: 'pointer' }}
				checked={active}
			/>
		</Group>
	);

}

// Main option (all)
const MainOption = ({ checked, selected, setChecked }) => {

	// State and hooks
	const { classes, cx } = useStyles();

	// Trigger selection of all elements
	const triggerAll = () => {

		// If all are not selected, do nothing
		if (selected[0].length === 0) return;

		// De-select all elements
		selected[1]([]);

		// Check "all"
		setChecked(true);

	}

	return (
		<Group
			className={cx(classes.optionRow, { [classes.isSelected]: checked })}
			position="apart"
			onClick={triggerAll}
		>
			{/* Label */}
			<Text
				className={classes.optionLabel}
				fw="bold"
			>
				Todos
			</Text>
			{/* Checkbox */}
			<Checkbox
				size="xs"
				style={{ cursor: 'pointer' }}
				checked={checked}
				onChange={() => {}}
			/>
		</Group>
	);

}

const BenefitPerformanceFilterSelector = ({ options, selected, orderByPerformance, performanceOrder, resetFilters, statusFilter, filterByStatus, userFilter, filterByUser, levelFilter, filterByLevel }) => {

	// State and hooks
	const [opened, setOpened] = useState(false);
	const { classes, cx } = useStyles();
	const [checked, setChecked] = useState(true);

	// Listen for changes
	useEffect(() => {

		// If one is checked, disable "all"
		if (selected && selected[0].length !== 0) setChecked(false);

		// If all are unchecked, enable "all"
		if (selected && selected[0].length === 0) setChecked(true);

	}, [selected]);

	// Each option
	const eachOption = (val, i) => {

		return (
			<Group
				className={cx(classes.optionRow, { [classes.isSelected]: selected[0].includes(val) })}
				key={i}
				position="apart"
				onClick={() => selected[1](els => els.includes(val) ? els.filter(item => item !== val) : [...els, val])}
			>
				{/* Label */}
				<Text className={classes.optionLabel}>
					{val}
				</Text>
				{/* Checkbox */}
				<Checkbox
					size="xs"
					style={{ cursor: 'pointer' }}
					checked={selected[0].includes(val)}
				/>
			</Group>
		);

	}
	
	return (
		<Popover
			opened={opened}
			onChange={setOpened}
			shadow="md"
			width={230}
			position="bottom-end"
			withArrow
			offset={3}
			arrowOffset={10}
		>
			{/* Opener */}
			<Popover.Target>
				{/* Purple Circle */}
				<Indicator
					offset={6}
					size={14}
					withBorder
					disabled
				>
					{/* Icon */}
					<ActionIcon
						variant="white"
						onClick={() => setOpened((o) => !o)}
					>
						<IconAdjustmentsHorizontal
							stroke={1.7}
							size={22}
						/>
					</ActionIcon>
				</Indicator>
			</Popover.Target>
			{/* Content */}
			<Popover.Dropdown
				p={0}
				pb={8}
			>
				{/* Top */}
				<Group position="apart">
					{/* Label */}
					<Text className={classes.label}>
						Filtrar por
					</Text>
					{/* Reset */}
					<Anchor
						type="button"
						size="xs"
						pr={12}
						onClick={resetFilters}
					>
						Limpiar filtros
					</Anchor>
				</Group>
				<Divider />
				{/* Category */}
				<Text className={classes.label}>
					Categoría
				</Text>
				<MainOption
					checked={checked}
					selected={selected}
					setChecked={setChecked}
				/>
				{options?.categories?.map(eachOption)}
				{/* Performance */}
				<Text className={classes.label}>
					Rendimiento
				</Text>
				{/* Options */}
				<Option
					text="Mayor rendimiento"
					action={() => orderByPerformance('more')}
					active={performanceOrder === 'more'}
				/>
				<Option
					text="Menor rendimiento"
					action={() => orderByPerformance('less')}
					active={performanceOrder === 'less'}
				/>
				{/* Level */}
				<Text className={classes.label}>
					Nivel
				</Text>
				{/* Options */}
				<Option
					text="Todos"
					action={() => filterByLevel('1, 2, 3')}
					active={levelFilter === '1, 2, 3'}
				/>
				<Option
					text="Nivel 01"
					action={() => filterByLevel('1')}
					active={levelFilter === '1'}
				/>
				<Option
					text="Nivel 02"
					action={() => filterByLevel('2')}
					active={levelFilter === '2'}
				/>
				<Option
					text="Nivel 03"
					action={() => filterByLevel('3')}
					active={levelFilter === '3'}
				/>
				{/* User */}
				<Text className={classes.label}>
					Usuario
				</Text>
				{/* Options */}
				<Option
					text="Todos"
					action={() => filterByUser('1, 2')}
					active={userFilter === '1, 2'}
				/>
				<Option
					text="Urbano"
					action={() => filterByUser('1')}
					active={userFilter === '1'}
				/>
				<Option
					text="Profesional"
					action={() => filterByUser('2')}
					active={userFilter === '2'}
				/>
				{/* Status */}
				<Text className={classes.label}>
					Estado
				</Text>
				{/* Options */}
				<Option
					text="Todos"
					action={() => filterByStatus('true, false')}
					active={statusFilter === 'true, false'}
				/>
				<Option
					text="Activos"
					action={() => filterByStatus('true')}
					active={statusFilter === 'true'}
				/>
				<Option
					text="Inactivos"
					action={() => filterByStatus('false')}
					active={statusFilter === 'false'}
				/>
			</Popover.Dropdown>
		</Popover>
	);

}

export default BenefitPerformanceFilterSelector;
